import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
} from '../../components/ui/card.tsx'
import { Alert, AlertDescription } from '../../components/ui/alert.tsx'
import { getUserSubscriptions } from '../../lib/stripe.ts'
import { formatDate } from '../../lib/utils.ts'
import type { SubscriptionDetails } from '../../lib/api/types.ts'

export function ActiveSubscriptions() {
  const { t } = useTranslation()
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<SubscriptionDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      const data = await getUserSubscriptions()
      setSubscriptionDetails(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manage.error'))
    } finally {
      setIsLoading(false)
    }
  }

  const getSubscriptionFeatures = (tier: string): Array<string> => {
    if (tier === 'pro') {
      return [
        t('subscriptions.features.pro_feature_1'),
        t('subscriptions.features.pro_feature_2'),
        t('subscriptions.features.pro_feature_3'),
      ]
    } else if (tier === 'premium') {
      return [
        t('subscriptions.features.premium_feature_1'),
        t('subscriptions.features.premium_feature_2'),
        t('subscriptions.features.premium_feature_3'),
        t('subscriptions.features.premium_feature_4'),
        t('subscriptions.features.premium_feature_5'),
      ]
    }
    return []
  }

  const getSubscriptionTierName = (tier: string) => {
    switch (tier) {
      case 'premium':
        return t('subscriptions.premiumSubscription')
      case 'pro':
        return t('subscriptions.proSubscription')
      default:
        return t('subscriptions.freeSubscription')
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-[#6D6D6D]">{t('manage.loading')}</div>
      </div>
    )
  }

  if (error && !subscriptionDetails) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  const { subscription, entitlements } = subscriptionDetails || {
    subscription: null,
    entitlements: {
      subscription_tier: 'free' as const,
      has_active_subscription: false,
      max_reading_lesson: 6,
    },
  }

  const currentTier = entitlements.subscription_tier
  const hasActiveSubscription = entitlements.has_active_subscription === true
  const features = getSubscriptionFeatures(currentTier)

  const currentPeriodEnd = subscription?.current_period_end ?? null
  const cancelAtPeriodEnd =
    subscription?.cancel_at_period_end ??
    subscription?.core_cancel_at_period_end ??
    false
  const gracePeriodEndsAt =
    subscription?.grace_period_ends_at ??
    subscription?.core_grace_period_ends_at ??
    null
  const commitmentMonths =
    subscription?.commitment_months ??
    subscription?.core_commitment_months ??
    null
  // UK subscriptions are prepaid grants: "cancel at period end" just means the
  // fixed-term access expires on that date, not a user-initiated cancellation.
  const isUkPrepaid = subscription?.stripe_account === 'uk'

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold mb-2">
          {t('subscriptions.yourSubscriptions')}
        </h1>
        <p className="text-sm sm:text-base text-[#6D6D6D]">
          {t('subscriptions.manageDescription')}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!hasActiveSubscription ? (
        <Card className="border border-[#E9E8F1]">
          <CardContent className="py-12 text-center">
            <p className="text-[#6D6D6D]">
              {t('subscriptions.noActiveSubscriptions')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="max-w-lg">
          <Card className="border-2 border-[#26247B]">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full bg-[#E9E8F1] text-[#363636]">
                {t('subscriptions.currentPlan')}
              </span>
              <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full text-[#26247B]">
                {t('subscriptions.subscriptionStatus.active')}
              </span>
            </CardHeader>

            <CardContent>
              <h2 className="text-2xl font-bold text-[#363636] mb-4">
                {getSubscriptionTierName(currentTier)}
              </h2>

              {/* Subscription Details */}
              {(commitmentMonths || currentPeriodEnd || gracePeriodEndsAt || cancelAtPeriodEnd) && (
                <div className="mb-4 space-y-2 text-sm border-b pb-4">
                  {!!commitmentMonths && (
                    <div className="flex justify-between">
                      <span className="text-[#6D6D6D]">
                        {t('subscriptions.commitment')}
                      </span>
                      <span>
                        {commitmentMonths === 1
                          ? t('subscriptions.monthlyNoCommitment')
                          : t('subscriptions.commitmentMonths', {
                              count: commitmentMonths,
                            })}
                      </span>
                    </div>
                  )}
                  {currentPeriodEnd && (
                    <div className="flex justify-between">
                      <span className="text-[#6D6D6D]">
                        {t('subscriptions.currentPeriodEnds')}
                      </span>
                      <span>{formatDate(currentPeriodEnd)}</span>
                    </div>
                  )}
                  {gracePeriodEndsAt && (
                    <div className="text-[#CD5B52]">
                      {t('subscriptions.gracePeriodEnds')}{' '}
                      {formatDate(gracePeriodEndsAt)}
                    </div>
                  )}
                  {cancelAtPeriodEnd &&
                    (isUkPrepaid ? (
                      currentPeriodEnd && (
                        <div className="flex justify-between">
                          <span className="text-[#6D6D6D]">
                            {t('subscriptions.accessUntil', {
                              date: formatDate(currentPeriodEnd),
                            })}
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="text-[#CD5B52]">
                        {t('subscriptions.willBeCanceled')}
                      </div>
                    ))}
                </div>
              )}

              {/* Features List */}
              <ul className="space-y-2">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#26247B]" />
                    <span className="text-sm text-[#363636]">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
